import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Input from 'components/inputs/Input';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useUpsertDailyBudgetOverride from './useUpsertDailyBudgetOverride';

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const MyInput = styled(Input)`
  padding-right: 5px;
  margin: 0;
  text-align: right;
  width: 60px;
  height: 18px;
  font-style: normal;
`;

interface Props {
  value?: number;
  date: Date;
  companyId: number;
  overrideId?: number;
  // round(weeklyBudget / 7) — used to recompute Amount when transferred is edited.
  defaultDailyAmount: number;
  plannedAmount?: number;
  onUpdate?: () => void;
  isFocused?: boolean;
}

const formatValue = (value?: number) => {
  if (value == null || value === 0) return '0';
  return value > 0 ? `+${value}` : `${value}`;
};

const TransferredFromPreviousDayInput: FC<Props> = ({
  value,
  date,
  companyId,
  overrideId,
  defaultDailyAmount,
  plannedAmount,
  onUpdate,
  isFocused,
}) => {
  const [cellValue, setCellValue] = useState<number>(value ?? 0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { upsert, isLoading } = useUpsertDailyBudgetOverride();

  useEffect(() => {
    setCellValue(value ?? 0);
  }, [value]);

  const handleChangeValue = useCallback(
    (newTransferred: number) => {
      if (newTransferred !== (value ?? 0)) {
        // Recompute Amount = defaultDailyAmount + transferred so today's budget reflects the correction.
        upsert(
          {
            companyId,
            date,
            overrideId,
            amount: defaultDailyAmount + newTransferred,
            transferredFromPreviousDay: newTransferred,
            plannedAmount,
          },
          onUpdate
        );
      }
    },
    [
      value,
      upsert,
      companyId,
      date,
      overrideId,
      defaultDailyAmount,
      plannedAmount,
      onUpdate,
    ]
  );

  useEffect(() => {
    if (isFocused) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => handleChangeValue(cellValue), 3000);
    }
  }, [cellValue, handleChangeValue, isFocused]);

  if (!isFocused) {
    return (
      <span title="Överfört från gårdagen (editerbart — ändring justerar dagens budget)">
        {formatValue(value)}
      </span>
    );
  }

  return (
    <Wrapper title="Överfört från gårdagen (editerbart — ändring justerar dagens budget)">
      <MyInput
        type="number"
        disabled={isLoading}
        value={cellValue}
        onChange={(eve) => {
          const newNum = Number(eve.target.value);
          if (Number.isNaN(newNum)) {
            setCellValue(0);
          } else {
            setCellValue(newNum);
          }
        }}
        onKeyDown={(eve) => {
          if (eve.key === 'Enter') {
            handleChangeValue(cellValue);
          }
        }}
      />
      {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
    </Wrapper>
  );
};

export default TransferredFromPreviousDayInput;
